import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { addNotification } from 'src/actions/notificationActions';
import { NOTIFICATION_STATUS, FORM_POST_HEADER } from 'src/constants';

export const SET_LOADING = '@signUp/set-loading';
export const SET_EMAIL_ADDRESS = '@signUp/set-formEmailAddress';
export const SET_REFERRAL_CODE = '@signUp/set-referralCode';

export const SIGNUP_STEPS = '@signUp/signup-steps';
export const SIGNUP_STEPS_SUCCESS = '@signUp/signup-steps-success';
export const SIGNUP_STEPS_ERROR = '@signUp/signup-steps-error';

export const SET_NON_FORM_ERROR_MESSAGE = '@signUp/set-non-form-error';

export const CONFIRM_CONSULTANT = '@signUp/confirm-consultant';
export const CONFIRM_CONSULTANT_SUCCESS = '@signUp/confirm-consultant-success';
export const CONFIRM_CONSULTANT_ERROR = '@signUp/confirm-consultant-error';

export const UPDATE_FILTERS = '@signUp/update-filters';
export const UPDATE_FILTERS_SUCCESS = '@signUp/update-filters-success';
export const UPDATE_FILTERS_ERROR = '@signUp/update-filters-error';

export const SET_WATCHED_FORWARDING_VIDEO = '@signUp/set-watchedForwardingVideo';
export const SET_WATCHED_WELCOME_VIDEO = '@signUp/set-watchedWelcomeVideo';

export const GET_COMPANIES_LIST = '@signUp/get-companies-list';
export const GET_COMPANIES_LIST_SUCCESS = '@signUp/get-companies-list-success';
export const GET_COMPANIES_LIST_ERROR = '@signUp/get-companies-list-error';

export const GET_SIGNUP_SETTINGS = '@signUp/get-signup-settings';
export const GET_SIGNUP_SETTINGS_SUCCESS = '@signUp/get-signup-settings-success';
export const GET_SIGNUP_SETTINGS_ERROR = '@signUp/get-signup-settings-error';

export const setLoading = (loading) => ({
  type: SET_LOADING,
  payload: loading
});

export const setFormEmailAddress = (formEmailAddress) => ({
  type: SET_EMAIL_ADDRESS,
  payload: formEmailAddress
});

// Carries a referral code supplied in the signup URL
// (/signupnow/<referralCode>) into the signup state, where the Referral Code
// tab picks it up as its prefill.
export const setReferralCode = (referralCode) => ({
  type: SET_REFERRAL_CODE,
  payload: referralCode
});

export const signupSteps = (code, info, step, password, doWhenTry = false, doWhenCatch = false) => async (dispatch) => {
  try {
    dispatch({
      type: SIGNUP_STEPS
    });

    const response = await axios.post('/api/signup/signupSteps', {
      info,
      password,
      authorizationCode: code,
      stepExecuting: step,
    }, FORM_POST_HEADER);
    const {
      success,
    } = response?.data || {};

    if (!success) {
      // For the Add CP Forwarding Address step, call the callback with hasError flag
      // so the UI can show a modal instead of inline error + snackbar
      const {
        screenMessage: errorScreenMessage,
        needsReauth: errorNeedsReauth,
      } = response?.data || {};
      if (doWhenTry && errorScreenMessage) {
        // Don't show the default notification - let the modal handle it
        dispatch({
          type: SIGNUP_STEPS_ERROR,
          payload: response?.data || null,
        });
        // needsReauth used to be hardcoded false here, so a failure caused by a
        // dead Google grant could never reach the UI as one. (CON-1680)
        doWhenTry(!!errorNeedsReauth, true);
        return;
      }

      dispatch(addNotification(SIGNUP_STEPS_ERROR, 'Invalid Email Address', NOTIFICATION_STATUS.ERROR));
      dispatch({
        type: SIGNUP_STEPS_ERROR,
        // payload: response?.data?.screenMessage || null, // 2023-08-28 changed to this
        payload: response?.data || null,
      });
      return;
    }

    // Only show success notification for the final account creation step
    // to avoid multiple "Completed Step!" notifications during signup
    const { nextStep, needsReauth, screenMessage } = response?.data || {};
    if (nextStep === 'Sign-In' || nextStep === 'Watch Welcome Video') {
      dispatch(addNotification(SIGNUP_STEPS_SUCCESS, 'Account created successfully!', NOTIFICATION_STATUS.SUCCESS));
    }

    dispatch({
      type: SIGNUP_STEPS_SUCCESS,
      payload: response?.data || null,
    });

    // Call the callback with needsReauth flag and hasError flag (screenMessage indicates an error)
    if (doWhenTry) {
      doWhenTry(needsReauth, !!screenMessage);
    }
  } catch (e) {
    const {
      message,
      error,
      needsReauth: errorNeedsReauth,
    } = e?.response?.data || {};
    const {
      screenMessage,
    } = error || {};
    const { message: errorMessage } = error || {};
    const messages = {
      screenMessage: screenMessage || error?.screenMessage,
      message,
      needsReauth: !!errorNeedsReauth,
    };

    // For forwarding address errors, call the callback to show modal instead of snackbar
    if (doWhenTry && (screenMessage || error?.screenMessage)) {
      dispatch({
        type: SIGNUP_STEPS_ERROR,
        payload: messages || null,
      });
      // A dead Google grant comes back as a 500, so this catch is the path the
      // re-auth signal actually travels on. (CON-1680)
      doWhenTry(!!errorNeedsReauth, true);
      return;
    }

    dispatch(addNotification(SIGNUP_STEPS_ERROR, message || errorMessage || error || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: SIGNUP_STEPS_ERROR,
      // payload: screenMessage,
      payload: messages || null,
    });
    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

export const confirmIsAConsultant = (code, emailProvider, doWhenTry = false, doWhenCatch = false) => async (dispatch) => {
  try {
    dispatch({
      type: CONFIRM_CONSULTANT
    });

    const response = await axios.post('/api/signup/confirmIsAConsultant', { code, emailProvider });
    const {
      success,
    } = response?.data || {};

    if (!success) {
      dispatch(addNotification(CONFIRM_CONSULTANT_ERROR, 'Could Not Confirm Which Company You are With', NOTIFICATION_STATUS.ERROR));
      dispatch({
        type: CONFIRM_CONSULTANT_ERROR,
      });
      return;
    }

    dispatch(addNotification(CONFIRM_CONSULTANT_SUCCESS, 'Successfully Confirmed Your Company', NOTIFICATION_STATUS.SUCCESS));

    dispatch({
      type: CONFIRM_CONSULTANT_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry();
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(CONFIRM_CONSULTANT_ERROR, message || errorMessage || error || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: CONFIRM_CONSULTANT_ERROR,
    });
    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

export const updateFiltersRules = (code, emailProvider, doWhenTry = false, doWhenCatch = false) => async (dispatch) => {
  try {
    dispatch({
      type: UPDATE_FILTERS
    });

    const response = await axios.put('/api/signup/updateFiltersRules', { code, emailProvider });
    const {
      success,
    } = response?.data || {};

    if (!success) {
      dispatch(addNotification(UPDATE_FILTERS_ERROR, "Could Not (Fully) Update Your Email's Filters/Rules", NOTIFICATION_STATUS.ERROR));
      dispatch({
        type: UPDATE_FILTERS_ERROR,
      });
      return;
    }

    dispatch(addNotification(UPDATE_FILTERS_SUCCESS, "Successfully Updated Your Email's Filters/Rules", NOTIFICATION_STATUS.SUCCESS));

    dispatch({
      type: UPDATE_FILTERS_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry();
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(UPDATE_FILTERS_ERROR, message || errorMessage || error || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: UPDATE_FILTERS_ERROR,
    });
    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

export const getCompanies = () => async (dispatch) => {
  dispatch({
    type: GET_COMPANIES_LIST,
  });
  try {
    const response = await axios.get('/api/signup/getcompanieslist', FORM_POST_HEADER);
    dispatch({
      type: GET_COMPANIES_LIST_SUCCESS,
      payload: response?.data?.companies || []
    });
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(GET_COMPANIES_LIST_ERROR, helpers.transform.toString(message || error || 'Server error'), NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: GET_COMPANIES_LIST_ERROR,
    });
  }
};

export const setWatchedForwardingVideo = () => ({
  type: SET_WATCHED_FORWARDING_VIDEO,
});

export const setWatchedWelcomeVideo = () => ({
  type: SET_WATCHED_WELCOME_VIDEO,
});

export const setNonFormError = () => ({
  type: SET_NON_FORM_ERROR_MESSAGE,
});

export const getSignupSettings = () => async (dispatch) => {
  dispatch({
    type: GET_SIGNUP_SETTINGS,
  });
  try {
    const response = await axios.get('/api/signup/getSignupSettings', FORM_POST_HEADER);
    dispatch({
      type: GET_SIGNUP_SETTINGS_SUCCESS,
      payload: response?.data || {}
    });
  } catch (e) {
    // Silently fail - fallback to showing all options
    dispatch({
      type: GET_SIGNUP_SETTINGS_ERROR,
    });
  }
};
